/* ==========================================================================
   Premium Reveal — assets/premium-reveal.js
   One IntersectionObserver, one init, no scroll listeners.
   Safe by design: content is only hidden after this script is running, and
   every failure path removes the hidden state again.

   Author-time API (all optional):
     data-reveal                 -> fade + rise          (same as "up")
     data-reveal="zoom"          -> fade + rise + image settles from slight zoom
     data-reveal="media"         -> fade + image settles (no movement)
     data-reveal="fade"          -> fade only
     data-reveal="none"          -> never animate this element or its children
     data-reveal-delay="200"     -> extra delay in ms
     data-reveal-group           -> on a parent: direct children rise one by one
   ========================================================================== */
(function () {
  'use strict';

  if (window.PremiumReveal) return; // never initialise twice

  var CONFIG = {
    cardDirection: 'down',       // product / collection cards: 'down' = slide in from above, 'up' = rise from below
    animateInThemeEditor: false, // false = content always visible in the Shopify editor
    stagger: 100,                // ms between items revealed in the same batch
    maxStaggerSteps: 24,         // every card in the batch gets its own delay (4 cols x 6 rows)
    rootMargin: '0px 0px -8% 0px',
    cleanupAfter: 1700           // ms after reveal, classes are removed again
  };

  var root = document.documentElement;
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var inEditor = !!(window.Shopify && window.Shopify.designMode);

  if (!('IntersectionObserver' in window) || !('MutationObserver' in window) ||
      reduceMotion || (inEditor && !CONFIG.animateInThemeEditor)) {
    window.PremiumReveal = { enabled: false };
    return;
  }

  /* Never animate inside these (sliders, drawers, modals, header, etc.). */
  var EXCLUDE = [
    'header', '.shopify-section-group-header-group', '.shopify-section-group-overlay-group',
    '.announcement-bar', 'slideshow-component', '.slideshow',
    'cart-drawer', '.cart-drawer', 'cart-notification', '.header-drawer', 'details-modal',
    'modal-dialog', '.predictive-search', 'product-modal', '.quick-add-modal', '.mobile-facets',
    '.facets-container-drawer', '[data-reveal="none"]'
  ].join(',');

  var CARD_INNER = '.card-wrapper, .card, .multicolumn-card, .article-card';
  var TYPES = {
    up: ['pr-up'],
    zoom: ['pr-up', 'pr-zoom'],
    zoomdown: ['pr-down', 'pr-zoom'], // slides in from above
    fadezoom: ['pr-zoom'], // no movement: used inside active horizontal sliders
    media: ['pr-media'],
    fade: []
  };

  /* A translate inside an overflow-x scroller can create a vertical scrollbar,
     so cards in an *active* slider (mobile/tablet) fade + zoom without rising. */
  var sliderState = new Map();
  function canRise(el) {
    var s = el.closest('.slider');
    if (!s) return true;
    if (!sliderState.has(s)) {
      var ox = window.getComputedStyle(s).overflowX;
      sliderState.set(s, !(ox === 'auto' || ox === 'scroll'));
    }
    return sliderState.get(s);
  }

  /* Selector map. First match wins, so explicit data-reveal comes first. */
  var RULES = [
    { sel: '[data-reveal]', type: function (el) { return el.getAttribute('data-reveal') || 'up'; }, force: true },
    { sel: '[data-reveal-group] > *', type: 'up', force: true },
    {
      sel: '.grid__item, .collection-list__item, .slider__slide',
      type: function (el) {
        if (!canRise(el)) return 'fadezoom';
        return CONFIG.cardDirection === 'up' ? 'zoom' : 'zoomdown';
      },
      test: function (el) {
        return !el.matches('.product__media-wrapper, .product__info-wrapper') && !!el.querySelector(CARD_INNER);
      }
    },
    /* Homepage custom sections: each product / review card staggers on its own.
       Cards inside horizontal tracks fade + zoom only (no vertical movement). */
    {
      sel: '.vh-card, .rws-card, .lr-card, .fs-item',
      type: function (el) {
        if (el.closest('.vh-track, .rws-track, [class*="track"], .slider')) return 'fadezoom';
        return CONFIG.cardDirection === 'up' ? 'zoom' : 'zoomdown';
      }
    },
    { sel: '.vh-heading, .rws-heading, .lr-heading', type: 'up' },
    { sel: '.product__media-wrapper', type: 'zoom' },
    { sel: '.product__info-wrapper', type: 'up' },
    { sel: '.banner__media, .video-section__media, .image-with-text__media', type: 'media' },
    {
      sel: '.banner__box, .image-with-text__content, .rich-text__blocks > *, ' +
           '.title-wrapper-with-link, .title-wrapper, .main-page-title, .collection-hero__inner, ' +
           '.newsletter__wrapper, .email-signup-banner__box, .footer-block, .footer__content-bottom',
      type: 'up'
    }
  ];

  var io, mo, pending = [], flushTimer = null, scanTimer = null;

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.top < window.innerHeight && r.bottom > 0 && r.width > 0;
  }

  function wanted(el, force) {
    if (el.hasAttribute('data-pr')) return false;
    if (el.closest(EXCLUDE)) return false;
    if (!force) {
      // Leave anything Dawn's own scroll-trigger system already animates alone.
      if (el.closest('.scroll-trigger') || el.querySelector('.scroll-trigger')) return false;
    }
    // Already inside a pending animated parent: it will arrive with it.
    var p = el.parentElement;
    if (p && p.closest('[data-pr="1"]')) return false;
    return true;
  }

  function prepare(el, type, force) {
    if (!TYPES[type]) type = 'up';
    if (!wanted(el, force)) return false;
    el.setAttribute('data-pr', '1');
    el.classList.add('pr-hide');
    TYPES[type].forEach(function (c) { el.classList.add(c); });
    // Above the fold at load: keep opacity 1 so the LCP image is never hidden.
    if ((type === 'media' || type === 'zoom' || type === 'zoomdown' || type === 'fadezoom') &&
        !root.classList.contains('pr-on') && inViewport(el)) {
      el.classList.add('pr-soft');
    }
    io.observe(el);
    return true;
  }

  function isContentSection(s) {
    if (!s.matches || !s.matches('.shopify-section')) return false;
    return (s.parentElement && s.parentElement.id === 'MainContent') || s.matches('.shopify-section-group-footer-group');
  }

  function hasContent(el) {
    return !!el.querySelector('img, svg, video, picture, iframe, canvas') || el.textContent.trim().length > 20;
  }

  /* Custom Liquid sections nobody mapped: animate their inner container. */
  function fallback(scope) {
    var sections = [];
    if (scope !== document && isContentSection(scope)) sections.push(scope);
    Array.prototype.forEach.call(scope.querySelectorAll('.shopify-section'), function (s) {
      if (isContentSection(s)) sections.push(s);
    });
    sections.forEach(function (s) {
      if (s.querySelector('[data-pr], .scroll-trigger, slideshow-component, .slideshow')) return;
      var target = s.querySelector('.page-width, .page-width-desktop, .page-width-tablet');
      if (!target && s.firstElementChild && s.firstElementChild.firstElementChild) {
        target = s.firstElementChild.firstElementChild;
      }
      if (target && target.tagName !== 'STYLE' && target.tagName !== 'SCRIPT' && hasContent(target)) {
        prepare(target, 'up', false);
      }
    });
  }

  function scan(scope) {
    scope = scope || document;
    sliderState.clear();
    var found = [], seen = new Set();
    RULES.forEach(function (rule) {
      Array.prototype.forEach.call(scope.querySelectorAll(rule.sel), function (el) {
        if (seen.has(el)) return;
        if (rule.test && !rule.test(el)) return;
        seen.add(el);
        found.push({
          el: el,
          type: typeof rule.type === 'function' ? rule.type(el) : rule.type,
          force: !!rule.force
        });
      });
    });
    // Outer elements first, so nested ones are correctly skipped.
    found.sort(function (a, b) {
      if (a.el === b.el) return 0;
      return (a.el.compareDocumentPosition(b.el) & 4) ? -1 : 1;
    });
    found.forEach(function (f) { prepare(f.el, f.type, f.force); });
    fallback(scope);
  }

  function cleanup(el) {
    el.classList.remove('pr-hide', 'pr-in', 'pr-up', 'pr-down', 'pr-zoom', 'pr-media', 'pr-soft');
    el.style.removeProperty('--pr-delay');
    el.setAttribute('data-pr', 'done');
  }

  function reveal(el, delay) {
    if (el.getAttribute('data-pr') !== '1') return;
    el.setAttribute('data-pr', 'run');
    el.style.setProperty('--pr-delay', delay + 'ms');
    el.classList.add('pr-in');
    setTimeout(function () { cleanup(el); }, delay + CONFIG.cleanupAfter);
  }

  function flush() {
    flushTimer = null;
    var batch = pending.splice(0).filter(function (el) { return el.isConnected; });
    var rects = new Map();
    batch.forEach(function (el) { rects.set(el, el.getBoundingClientRect()); });
    batch.sort(function (a, b) {
      var ra = rects.get(a), rb = rects.get(b);
      return Math.abs(ra.top - rb.top) > 24 ? ra.top - rb.top : ra.left - rb.left;
    });
    batch.forEach(function (el, i) {
      var extra = parseInt(el.getAttribute('data-reveal-delay'), 10) || 0;
      reveal(el, Math.min(i, CONFIG.maxStaggerSteps) * CONFIG.stagger + extra);
    });
  }

  function onIntersect(entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) {
        io.unobserve(e.target);
        pending.push(e.target);
      }
    });
    if (pending.length && !flushTimer) flushTimer = setTimeout(flush, 40);
  }

  /* Failsafe: anything on screen that is still hidden gets shown. */
  function sweep() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-pr="1"]'), function (el) {
      if (inViewport(el)) { io.unobserve(el); reveal(el, 0); }
    });
  }

  function revealWithin(node) {
    if (!node || !node.querySelectorAll) return;
    var list = Array.prototype.slice.call(node.querySelectorAll('[data-pr="1"]'));
    if (node.getAttribute && node.getAttribute('data-pr') === '1') list.unshift(node);
    list.forEach(function (el) { io.unobserve(el); reveal(el, 0); });
  }

  function abort() {
    root.classList.remove('pr-on');
    Array.prototype.forEach.call(document.querySelectorAll('[data-pr]'), cleanup);
  }

  function scheduleScan() {
    clearTimeout(scanTimer);
    scanTimer = setTimeout(function () { scan(document); }, 200);
  }

  function init() {
    try {
      io = new IntersectionObserver(onIntersect, { rootMargin: CONFIG.rootMargin, threshold: 0 });
      scan(document);
      root.classList.add('pr-on'); // same task as the scan: no flash of hidden state

      // Product grids re-rendered by filters, recommendations, quick add, apps...
      mo = new MutationObserver(function (muts) {
        var relevant = muts.some(function (m) {
          return m.addedNodes.length && !(m.target.closest && m.target.closest(EXCLUDE));
        });
        if (relevant) scheduleScan();
      });
      mo.observe(document.body, { childList: true, subtree: true });

      setTimeout(sweep, 2500);
      setTimeout(sweep, 8000);
      window.addEventListener('pageshow', function (e) { if (e.persisted) sweep(); });
    } catch (err) {
      abort();
    }
  }

  /* Theme editor (only reached when animateInThemeEditor is true). */
  document.addEventListener('shopify:section:load', function (e) { if (io) scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    if (!io) return;
    Array.prototype.forEach.call(e.target.querySelectorAll('[data-pr="1"]'), function (el) { io.unobserve(el); });
  });
  document.addEventListener('shopify:block:select', function (e) { revealWithin(e.target); });
  document.addEventListener('shopify:section:select', function (e) { revealWithin(e.target); });

  window.PremiumReveal = {
    enabled: true,
    scan: scan,
    refresh: function () { scan(document); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
